/**
 * Active / All / Reviewed toggle for the feed. Uncontrolled usage reads and
 * writes the shared `FeedContext` review filter so `/feed`, the home widget,
 * and the right-rail stay in sync. Pass `value` + `onChange` for a local
 * filter (booking activity) that must not touch the global feed.
 */
import { cn } from "@/lib/utils";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useFeedOptional } from "@/context/FeedContext";
import type { ReviewStatus } from "@/types/feed";
import "./feedFilterBar.css";

const REVIEW_STATUSES: { value: ReviewStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "all", label: "All" },
  { value: "reviewed", label: "Reviewed" },
];

function isReviewStatus(value: string): value is ReviewStatus {
  return value === "active" || value === "all" || value === "reviewed";
}

function chipClass(active: boolean): string {
  return cn("feed-filter-chip", active && "feed-filter-chip--active");
}

export function FeedReviewStatusToggle({
  className,
  fullWidth = false,
  compact = false,
  value,
  onChange,
  loading: loadingProp,
}: {
  className?: string;
  /** Full-width track for the narrow right-rail filter stack. */
  fullWidth?: boolean;
  /** Compact header control for the home activity widget. */
  compact?: boolean;
  /** Controlled value — booking activity keeps this independent of the global feed. */
  value?: ReviewStatus;
  onChange?: (next: ReviewStatus) => void;
  loading?: boolean;
}) {
  const feed = useFeedOptional();
  const reviewStatus = value ?? feed?.filters.reviewStatus ?? "active";
  const loading = loadingProp ?? feed?.loading ?? false;
  const setReviewStatus = (next: ReviewStatus) => {
    if (onChange) onChange(next);
    else feed?.setFilters({ reviewStatus: next });
  };

  if (compact) {
    return (
      <ToggleGroup
        type="single"
        value={reviewStatus}
        onValueChange={(next) => {
          if (!isReviewStatus(next)) return;
          setReviewStatus(next);
        }}
        track="clay"
        className={className}
        aria-label="Review status"
        aria-busy={loading || undefined}
      >
        {REVIEW_STATUSES.map(({ value: status, label }) => (
          <ToggleGroupItem key={status} value={status}>
            {label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    );
  }

  return (
    <div
      className={cn(
        "feed-filter-track min-w-0",
        fullWidth ? "w-full max-w-full" : "w-fit shrink-0",
        className,
      )}
      role="group"
      aria-label="Review status"
      aria-busy={loading || undefined}
    >
      {REVIEW_STATUSES.map(({ value: status, label }) => (
        <button
          key={status}
          type="button"
          aria-pressed={reviewStatus === status}
          onClick={() => setReviewStatus(status)}
          className={chipClass(reviewStatus === status)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
